import { MapToolBox } from "@/components/map-tool-box";
import { offStreetToolboxIcons } from "@/modules/offstreet-parking/components/off-street-map-tool-box-entry.const";
import { getAddress } from "@/services/web/mapbox.service";
import { CreateOffStreetParkingInput, UpdateOffStreetParkingInput } from "@/types/gql/graphql";
import { BitmapLayer, GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import { mapboxStyle, ModeViewType } from "constants/map.const";
import { MAPBOX_ACCESS_TOKEN } from "constants/mapbox.const";

import { CityContext } from "contexts/city-context-provider";
import { PickingInfo, TileLayer } from "deck.gl";
import { Point, Position } from "geojson";
import { createMarkerLayer } from "helper/map-utils";
import { isNil } from "helper/utils";
import { useAddControlMapbox } from "hooks/use-add-control-mapbox";
import { useQueryParamByViewPort } from "hooks/use-set-query-params";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Map as StaticMap, ViewState } from "react-map-gl/mapbox";
import { IconStation, IOffStreetParking, IOffStreetParkingSummary } from "types";
import { interactionStateType, MapType, mapUrlByType, ViewportPlaceType } from "types/map.type";
import { SearchPlace, SelectMapType, SwitchModeView } from "../curb-zone/components";
import { OffStreetParkingTooltip, parkingTooltipProps } from "./components/off-street-parking-tooltip";
import { OffStreetParkingDetail } from "./components/offstreet-parking-detail";
import { OffStreetParkingModal } from "./modes/add-off-street-parking/off-street-parking-modal";
import { columns } from "./off-street-parking-table/columns";
import { DataTable } from "./off-street-parking-table/data-table";
import { ICON_MAPPING, initOffStreetParking, OffStreetParkingModalState } from "./off-street-parking.const";
import { useOffStreetParking } from "./off-street-parking.hook";
import { PrivateParkingModeType } from "./off-street-parking.type";

export const OffstreetParkingMap = () => {
  const { currentCity, viewport, setViewport } = useContext(CityContext);
  const [mapType, setMapType] = useState<MapType>(MapType.MAPBOX);
  const [parkingModalState, setParkingModalState] = useState<keyof typeof OffStreetParkingModalState>(
    OffStreetParkingModalState.DEACTIVATE,
  );

  const [privateParkingMode, setPrivateParkingMode] = useState<PrivateParkingModeType>(PrivateParkingModeType.View);

  const [modeView, setModeView] = useState<ModeViewType>("normal");
  const [offStreetParkingDetail, setOffStreetParkingDetail] = useState<IOffStreetParking | null>();

  const currentPosition = useRef<Position>([]);
  const preZooming = useRef<ViewState | null>(null);
  const { mapRefCallback } = useAddControlMapbox();
  const setQueryParamByViewport = useQueryParamByViewPort();

  const {
    OffStreetParking,
    fetchOffStreetParking,
    createOffStreetParking,
    updateOffStreetParking,
    removeOffStreetParking,
  } = useOffStreetParking();

  const [hoverInfo, setHoverInfo] = useState<parkingTooltipProps>({} as parkingTooltipProps);

  const mapStyle = useMemo(() => mapboxStyle[modeView], [modeView]);
  useEffect(() => {
    if (isNil(currentCity)) return;
    if (currentCity?.position) {
      setOffStreetParkingDetail(initOffStreetParking);
      currentPosition.current = currentCity?.position;
    }
  }, [currentCity]);

  useEffect(() => {
    if (!currentCity) {
      return;
    }
    getOffStreetParkings(viewport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport, currentCity]);

  const offStreetParkingPoint = useMemo(() => {
    return OffStreetParking.map(({ location, ...rest }: IOffStreetParkingSummary) => ({
      ...rest,
      coordinates: location.coordinates,
    }));
  }, [OffStreetParking]);

  const offStreetParkingLayer = new IconLayer({
    id: "private-icon-layer",
    data: offStreetParkingPoint,
    pickable: true,
    iconAtlas: "images/private_parking_icon.png",
    iconMapping: ICON_MAPPING,
    getIcon: () => "marker",
    sizeScale: 15,
    getPosition: (d: IconStation) => d.coordinates,
    getSize: () => 3,
    onHover: (info) => {
      if (privateParkingMode !== PrivateParkingModeType.View) {
        return;
      }
      if (info.object) {
        if (
          !hoverInfo.offStreetParking ||
          (hoverInfo?.offStreetParking && info.object.id !== hoverInfo?.offStreetParking.id)
        ) {
          setHoverInfo({
            left: info.x,
            top: info.y,
            offStreetParking: info.object,
          });
        }
      } else if (!info.object) {
        setHoverInfo({} as parkingTooltipProps);
      }
    },
    updateTriggers: {
      onHover: [privateParkingMode],
    },
  });

  const markerLayer = useMemo(() => {
    if (!offStreetParkingDetail?.location?.coordinates?.length) {
      return null;
    }
    return createMarkerLayer({
      coordinates: [offStreetParkingDetail.location.coordinates[0], offStreetParkingDetail.location.coordinates[1]],
    });
  }, [offStreetParkingDetail]);

  const currentCityLayer = new GeoJsonLayer({
    id: "current-city",
    data: currentCity?.boundaries,
    filled: false,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 4,
  });

  const tileLayer = new TileLayer({
    id: "sat-other-map",
    data: mapUrlByType[mapType],

    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,

    renderSubLayers: (props) => {
      const { boundingBox } = props.tile;

      return new BitmapLayer(props, {
        data: undefined,
        image: props.data,
        bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]],
      });
    },
    visible: mapType !== MapType.MAPBOX,
  });

  const offStreetParkingMapLayers = [tileLayer, offStreetParkingLayer, currentCityLayer, markerLayer];

  const getCursor = () => {
    if (privateParkingMode === PrivateParkingModeType.AddPrivateParking) {
      return () => "cell";
    }
    return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
  };

  const handleDragEnd = (event: PickingInfo) => {
    if (!event.viewport) {
      return;
    }
    const viewport = event.viewport as unknown as ViewState;
    const newPosition = [viewport?.longitude, viewport?.latitude];
    currentPosition.current = [...newPosition];
    getOffStreetParkings(viewport);
  };

  const getOffStreetParkings = (viewState: ViewState) => {
    if (!currentCity || !viewState) return;
    setQueryParamByViewport({ viewState, viewport, cityID: currentCity.id });
  };

  const handleZooming = (input: interactionStateType) => {
    if (preZooming.current && !input.isZooming) {
      getOffStreetParkings(preZooming.current);
    }
  };

  const onViewStateChange = (viewState: ViewState) => {
    preZooming.current = viewState;
  };

  const selectAddress = (viewport: ViewportPlaceType | null) => {
    if (viewport) {
      setOffStreetParkingDetail(
        (prevState) =>
          ({
            ...prevState,
            address: viewport.name,
            location: { type: "Point", coordinates: [Number(viewport.longitude), Number(viewport.latitude)] },
          }) as IOffStreetParking,
      );

      setViewport((prevState) => ({ ...prevState, ...viewport, zoom: 16 }));
    }
  };

  const handleSelectOffStreetParking = async (info: PickingInfo) => {
    if (parkingModalState === OffStreetParkingModalState.MINIMIZE) {
      setOffStreetParkingDetail({
        ...offStreetParkingDetail,
        location: { type: "Point", coordinates: info.coordinate },
      } as IOffStreetParking);
      return;
    }
    if (info.object && info.object.id) {
      const parking = await fetchOffStreetParking(info.object.id);
      setOffStreetParkingDetail(parking);
      if (parkingModalState === OffStreetParkingModalState.DEACTIVATE) {
        setParkingModalState(OffStreetParkingModalState.SHOW);
      }
      return;
    }
    if (parkingModalState === OffStreetParkingModalState.DEACTIVATE) {
      const newPoint = { type: "Point", coordinates: info.coordinate } as Point;
      const address = await getAddress(newPoint);
      setOffStreetParkingDetail({
        ...initOffStreetParking,
        address: address.name ?? "",
        location: newPoint,
      });
      setParkingModalState(OffStreetParkingModalState.SHOW);
    }
  };

  const handleClose = () => {
    setParkingModalState(OffStreetParkingModalState.DEACTIVATE);
    setOffStreetParkingDetail(initOffStreetParking);
  };

  const saveOffStreetParking = async (input: IOffStreetParking) => {
    const totalPlaces = Number(input.totalPlaces);
    const pricePerHour = Number(input.pricePerHour);
    const newOffStreetParking = { ...input, cityId: currentCity?.id, totalPlaces, pricePerHour } as IOffStreetParking;
    if (input.id) {
      await updateOffStreetParking({
        variables: { input: newOffStreetParking as UpdateOffStreetParkingInput },
      });
    } else {
      await createOffStreetParking({
        variables: { input: newOffStreetParking as CreateOffStreetParkingInput },
      });
    }
    handleClose();
  };

  const handleRemoveOffStreetParking = async () => {
    if (!offStreetParkingDetail?.id) {
      return;
    }
    await removeOffStreetParking({ variables: { id: offStreetParkingDetail.id } });
    setOffStreetParkingDetail(initOffStreetParking);
  };

  const handleRowClick = async (parking: IOffStreetParkingSummary) => {
    if (parking.location && parking.location.coordinates) {
      setViewport((prevState) => ({
        ...prevState,
        longitude: parking.location.coordinates[0],
        latitude: parking.location.coordinates[1],
        zoom: 16,
      }));

      setOffStreetParkingDetail(await fetchOffStreetParking(parking.id));
    }
  };

  return (
    <div className="relative h-full">
      <DeckGL
        initialViewState={{ ...viewport }}
        controller={{
          touchZoom: true,
          touchRotate: true,
        }}
        getCursor={getCursor()}
        layers={offStreetParkingMapLayers}
        pickingRadius={12}
        onDragEnd={handleDragEnd}
        onInteractionStateChange={handleZooming}
        onClick={handleSelectOffStreetParking}
        onViewStateChange={({ viewState }) => onViewStateChange(viewState as ViewState)}
      >
        <StaticMap mapboxAccessToken={MAPBOX_ACCESS_TOKEN} mapStyle={mapStyle} ref={mapRefCallback} />
        {parkingModalState !== OffStreetParkingModalState.MINIMIZE && hoverInfo?.offStreetParking && (
          <OffStreetParkingTooltip
            left={hoverInfo.left}
            top={hoverInfo.top}
            offStreetParking={hoverInfo.offStreetParking}
          />
        )}
      </DeckGL>
      <div className="absolute top-16 left-6 z-6 flex gap-2">
        <SearchPlace onSelect={selectAddress} coordinates={viewport} />
        <SelectMapType mapType={mapType} onSelectMapType={(value) => setMapType(value)} />
      </div>
      <SwitchModeView modeView={modeView} selectModeView={(mode: ModeViewType) => setModeView(mode)} />
      <div className="absolute top-60 left-8 z-6">
        <MapToolBox
          appMode={privateParkingMode}
          setAppMode={setPrivateParkingMode}
          disabled={parkingModalState !== OffStreetParkingModalState.DEACTIVATE}
          toolboxIcons={offStreetToolboxIcons}
        />
      </div>
      {parkingModalState !== OffStreetParkingModalState.DEACTIVATE && offStreetParkingDetail && (
        <OffStreetParkingModal
          coordinates={viewport}
          offStreetParking={offStreetParkingDetail}
          onClose={handleClose}
          onSave={saveOffStreetParking}
          parkingModalState={parkingModalState}
          setParkingModalState={setParkingModalState}
          setOffStreetParkingDetail={setOffStreetParkingDetail}
        />
      )}

      {parkingModalState !== OffStreetParkingModalState.MINIMIZE &&
        offStreetParkingDetail &&
        offStreetParkingDetail.id && (
          <OffStreetParkingDetail
            setParkingModalState={setParkingModalState}
            parking={offStreetParkingDetail}
            removeParking={handleRemoveOffStreetParking}
            onClose={handleClose}
          />
        )}

      <div className="absolute top-25 right-6 flex ">
        <DataTable columns={columns} data={OffStreetParking} onRowClick={handleRowClick} />
      </div>
    </div>
  );
};
